import {
  GachaAmountMode,
  GachaBoxType,
  GachaRecoveryMode,
  GachaRefreshMode,
  GachaRewardType,
} from '../../../../../__gqltypes__';
import {
  GACHA_BOOST_TYPES,
  GACHA_REWARD_TYPES,
  GACHA_UNLIMITED_BOOST_TYPES,
  GachaBoxDraft,
  GachaCampaignDraft,
  GachaCountRowDraft,
  GachaMatcherDraft,
  GachaMysteryDraft,
  GachaPoolRowDraft,
  GachaRewardDraft,
  gachaMatcherLevel,
} from './utils';

// ---------------------------------------------------------------------------------------------------------------------
// Save-time validation, a rule-for-rule mirror of the server's (SP3BE gachaShop/validation.ts). The server stays the
// final authority (GACHA_ADMIN_INVALID_CONFIG); these only drive the Save button and the error list. Pure.
// ---------------------------------------------------------------------------------------------------------------------

const VERSION_PATTERN = /^\d+(\.\d+)*$/;
export const GACHA_MAX_NAME_LENGTH = 128;

const isPositiveFinite = (value: number) => typeof value === 'number' && Number.isFinite(value) && value > 0;
const isPositiveInteger = (value: number) => Number.isInteger(value) && value >= 1;
const isPercent = (value: number) => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100;

// A reward's identity: type + the one field that names the item, never the amount (the server's gachaRewardIdentity).
// Several rows may share one; "allow duplicates" and its count check are about identities, not rows.
export function gachaRewardIdentity(reward: GachaRewardDraft): string {
  switch (reward.type) {
    case GachaRewardType.CURRENCY_BOOST:
    case GachaRewardType.CURRENCY_CATEGORY_FRAGMENT:
    case GachaRewardType.CONTENT_PACK_REWARD:
    case GachaRewardType.CURRENCY_VANITY_PIECE:
      return `${reward.type}:${(reward.itemId ?? '').trim()}`;
    case GachaRewardType.CURRENCY_UNLIMITED_BOOST:
      return `${reward.type}:${reward.unlimitedBoostType ?? ''}`;
    default:
      return `${reward.type}`;
  }
}

export function validateGachaReward(reward: GachaRewardDraft, label: string): string[] {
  if (!reward?.type) {
    return [`${label}: the reward type is missing.`];
  }
  if (!GACHA_REWARD_TYPES.includes(reward.type)) {
    return [`${label}: ${reward.type} is not a Gacha Shop reward type.`];
  }
  const itemId = (reward.itemId ?? '').trim();
  switch (reward.type) {
    case GachaRewardType.CURRENCY_BOOST:
      return (GACHA_BOOST_TYPES as string[]).includes(itemId) ? [] : [`${label}: a Booster needs its booster type.`];
    case GachaRewardType.CURRENCY_UNLIMITED_BOOST:
      return reward.unlimitedBoostType && GACHA_UNLIMITED_BOOST_TYPES.includes(reward.unlimitedBoostType)
        ? []
        : [`${label}: an unlimited Booster needs its booster type.`];
    case GachaRewardType.CURRENCY_CATEGORY_FRAGMENT:
      return itemId ? [] : [`${label}: a Fragment needs its category.`];
    case GachaRewardType.CONTENT_PACK_REWARD:
      return itemId ? [] : [`${label}: a Playlist needs its playlist id.`];
    case GachaRewardType.CURRENCY_VANITY_PIECE:
      return itemId ? [] : [`${label}: Vanity Pieces need their piece type.`];
    default:
      return [];
  }
}

export function validateGachaPoolRows(rows: ReadonlyArray<GachaPoolRowDraft>, label: string): string[] {
  if (!rows || rows.length === 0) {
    return [`${label}: the table needs at least one reward.`];
  }
  const errors: string[] = [];
  rows.forEach((row, index) => {
    const rowLabel = `${label} row ${index + 1}`;
    errors.push(...validateGachaReward(row.reward, rowLabel));
    if (!isPositiveFinite(row.weight)) {
      errors.push(`${rowLabel}: the weight must be a number greater than 0.`);
    }
    if (row.amountMode === GachaAmountMode.EXACT) {
      if (!isPositiveInteger(row.amount)) {
        errors.push(`${rowLabel}: the exact amount must be a whole number of at least 1.`);
      }
    } else {
      if (!isPositiveInteger(row.minAmount)) {
        errors.push(`${rowLabel}: the minimum amount must be a whole number of at least 1.`);
      }
      if (!Number.isInteger(row.maxAmount) || row.maxAmount < row.minAmount) {
        errors.push(`${rowLabel}: the maximum amount must be a whole number, at least the minimum.`);
      }
    }
  });
  return errors;
}

// How many different rewards a table holds: two Coins rows are one reward.
export function gachaDistinctRewardCount(rows: ReadonlyArray<GachaPoolRowDraft>): number {
  return new Set(rows.map((row) => gachaRewardIdentity(row.reward))).size;
}

// The sum of the reward-count weights (information only: each row's chance is weight / total).
export function gachaCountWeightTotal(rows: ReadonlyArray<GachaCountRowDraft>): number {
  return rows.reduce((sum, row) => sum + (Number.isFinite(row.weight) ? row.weight : 0), 0);
}

export function validateGachaCountRows(rows: ReadonlyArray<GachaCountRowDraft>, label: string): string[] {
  if (!rows || rows.length === 0) {
    return [`${label}: the reward-count list needs at least one row.`];
  }
  const errors: string[] = [];
  rows.forEach((row, index) => {
    const rowLabel = `${label} row ${index + 1}`;
    if (!isPositiveInteger(row.count)) {
      errors.push(`${rowLabel}: the reward count must be a whole number of at least 1.`);
    }
    // A weight of 0 is allowed: that count never comes up.
    if (!(typeof row.weight === 'number' && Number.isFinite(row.weight) && row.weight >= 0)) {
      errors.push(`${rowLabel}: the weight must be a number of at least 0.`);
    }
  });
  if (!rows.some((row) => isPositiveFinite(row.weight))) {
    errors.push(`${label}: at least one row needs a weight greater than 0.`);
  }
  return errors;
}

export function validateGachaMatcher(matcher: GachaMatcherDraft, label: string): string[] {
  const errors: string[] = [];
  const { userMinCreationDate: min, userMaxCreationDate: max } = matcher;
  if (min != null && max != null && min > max) {
    errors.push(`${label}: the minimum registration date is after the maximum one.`);
  }
  const version = matcher.appVersion.trim();
  if (version && !VERSION_PATTERN.test(version)) {
    errors.push(`${label}: the build version must look like 003.020.000.`);
  }
  const level = gachaMatcherLevel(matcher);
  if (level != null && !(Number.isInteger(level) && level >= 0)) {
    errors.push(`${label}: the minimum user level must be a whole number of at least 0.`);
  }
  return errors;
}

// The largest count the box can actually roll, the number of distinct rewards one opening may need. A row with weight
// 0 is never drawn, so it does not count.
export function gachaMaxRolledCount(rows: ReadonlyArray<GachaCountRowDraft>): number {
  return Math.max(
    0,
    ...rows.filter((row) => isPositiveFinite(row.weight)).map((row) => (Number.isFinite(row.count) ? row.count : 0))
  );
}

export function validateGachaMystery(mystery: GachaMysteryDraft, label: string): string[] {
  if (!mystery.enabled) {
    return [];
  }
  const errors: string[] = [];
  if (!mystery.inherit) {
    errors.push(...validateGachaMatcher(mystery.criteria, `${label} matcher`));
  }
  if (!isPercent(mystery.firstRatePercent)) {
    errors.push(`${label}: the first drop rate must be between 0 and 100.`);
  }
  if (!isPercent(mystery.decreasePercent)) {
    errors.push(`${label}: the decrease per pull must be between 0 and 100.`);
  }
  if (!isPercent(mystery.minRatePercent)) {
    errors.push(`${label}: the minimum drop rate must be between 0 and 100.`);
  }
  if (
    isPercent(mystery.firstRatePercent) &&
    isPercent(mystery.minRatePercent) &&
    mystery.minRatePercent > mystery.firstRatePercent
  ) {
    errors.push(`${label}: the minimum drop rate cannot be above the first drop rate.`);
  }
  if (mystery.recoveryMode === GachaRecoveryMode.GLOBAL && !isPositiveInteger(mystery.recoveryIntervalSeconds ?? 0)) {
    errors.push(`${label}: the global recovery timer must be at least 1 second.`);
  }
  errors.push(...validateGachaPoolRows(mystery.pool, `${label} reward table`));
  return errors;
}

type CampaignWindow = { startDate: number; stopDate: number | null };

function validateDiscount(box: GachaBoxDraft, campaign: CampaignWindow, label: string): string[] {
  const errors: string[] = [];
  const { discountPercent: percent, discountStartDate: startDate, discountStopDate: stopDate } = box;
  if (!Number.isInteger(percent) || percent < 1 || percent > 99) {
    errors.push(`${label}: the discount must be a whole percentage between 1 and 99.`);
  }
  const campaignStart = campaign.startDate;
  const campaignStop = campaign.stopDate;
  if (startDate != null && startDate < campaignStart) {
    errors.push(`${label}: the discount cannot start before the campaign.`);
  }
  if (stopDate != null && campaignStop != null && stopDate > campaignStop) {
    errors.push(`${label}: the discount cannot end after the campaign.`);
  }
  if (startDate != null && stopDate != null && stopDate <= startDate) {
    errors.push(`${label}: the discount must end after it starts.`);
  }
  if (startDate != null && campaignStop != null && startDate >= campaignStop) {
    errors.push(`${label}: the discount must start before the campaign ends.`);
  }
  if (stopDate != null && stopDate <= campaignStart) {
    errors.push(`${label}: the discount must end after the campaign starts.`);
  }
  return errors;
}

export function gachaBoxLabel(box: Pick<GachaBoxDraft, 'tier'>, index: number): string {
  return `Box ${index + 1} (${box.tier})`;
}

export function validateGachaBox(box: GachaBoxDraft, campaign: CampaignWindow, label: string): string[] {
  const errors: string[] = [];
  if (!isPositiveInteger(box.claimLimit)) {
    errors.push(`${label}: the claim limit must be a whole number of at least 1.`);
  }
  if (box.refreshMode === GachaRefreshMode.CUSTOM && !isPositiveInteger(box.refreshIntervalSeconds ?? 0)) {
    errors.push(`${label}: the refresh timer must be at least 1 second.`);
  }
  if (box.type === GachaBoxType.PAID) {
    if (!isPositiveInteger(box.priceAmount)) {
      errors.push(`${label}: the price must be a whole number of at least 1.`);
    }
    if (box.discountEnabled) {
      errors.push(...validateDiscount(box, campaign, label));
    }
  }
  errors.push(...validateGachaCountRows(box.countTable, `${label} reward-count list`));
  errors.push(...validateGachaPoolRows(box.rewards, `${label} reward table`));
  if (!box.allowDuplicates) {
    const maxCount = gachaMaxRolledCount(box.countTable);
    // Distinct reward identities, not rows: two Coins rows are one reward.
    const distinct = gachaDistinctRewardCount(box.rewards);
    if (maxCount > distinct) {
      errors.push(
        `${label}: without duplicates a box cannot give ${maxCount} rewards from ${distinct} distinct rewards.`
      );
    }
  }
  if (box.mystery) {
    errors.push(...validateGachaMystery(box.mystery, `${label} mystery reward`));
  }
  return errors;
}

export function validateGachaCampaignDraft(draft: GachaCampaignDraft): string[] {
  const errors: string[] = [];
  const name = draft.name.trim();
  if (!name) {
    errors.push('The campaign needs a name.');
  } else if (name.length > GACHA_MAX_NAME_LENGTH) {
    errors.push(`The campaign name is limited to ${GACHA_MAX_NAME_LENGTH} characters.`);
  }
  if (!Number.isFinite(draft.startDate)) {
    errors.push('The campaign needs a valid UTC start date.');
  }
  if (draft.stopDate != null) {
    if (!Number.isFinite(draft.stopDate)) {
      errors.push('The campaign end date is not a valid UTC date (leave it empty for a permanent campaign).');
    } else if (Number.isFinite(draft.startDate) && draft.stopDate <= draft.startDate) {
      errors.push('The campaign must end after it starts.');
    }
  }
  errors.push(...validateGachaMatcher(draft.matcher, 'Campaign matcher'));
  if (draft.enabled && draft.boxes.length === 0) {
    errors.push('An enabled campaign needs at least one box.');
  }
  draft.boxes.forEach((box, index) => {
    errors.push(...validateGachaBox(box, draft, gachaBoxLabel(box, index)));
  });
  return errors;
}

// Template names follow the server rule (saveGachaTemplate).
export function validateGachaTemplateName(name: string): string[] {
  const trimmed = name.trim();
  return !trimmed || trimmed.length > GACHA_MAX_NAME_LENGTH
    ? [`The template needs a name of at most ${GACHA_MAX_NAME_LENGTH} characters.`]
    : [];
}
